// Krapf Bus Company — static application configuration, same shape as config/peoria.js.
//
// Krapf has exactly one application: Charter Mail, an operator-facing inbox that reads the company's
// real Outlook mailbox over Microsoft Graph. An operator marks an inbound charter inquiry as a trip
// inquiry, which starts a Movorra Studio (ECAP) session on the `krapf-charter-intake` flow seeded
// with the email body; the page then composes the reply email from the flow's missing fields.
//
// Rules (asserted in applicationCatalog.test.js):
//   • Pure data. No React, no window, no process.env reads — env is injected into buildKrapfCompany(env).
//   • Nothing secret and no key VALUES. The flow's public runtime key is exposed only through
//     runtimeKeys (consumed by routing), never inside an application definition that a screen renders.

// Krapf staff groups, deliberately disjoint from R3's and Peoria's (see config/peoria.js — movorra-api
// enforces the same isolation on its side for peoria_*).
export const KRAPF_OPS_GROUPS = ['krapf_operations', 'krapf_admin'];

// The Studio flow the Charter Mail page runs. Informational in the frontend: the backend resolves the
// flow from the public endpoint key, not from this id.
export const KRAPF_INTAKE_FLOW_ID = 'krapf-charter-intake';

const BRANDING = {
  companyName: 'Krapf Bus Company', subtitle: 'CHARTER MAIL', logoSrc: null, isCharter: true,
};

export function buildKrapfCompany(env = {}) {
  const intakeKey = String(env.REACT_APP_KRAPF_ECAP_FLOW_KEY || '').trim();
  // SANDBOX ACCESS. Charter Mail is open to anyone ONLY while it is running against the mock
  // mailbox — a fake inbox, seeded with invented inquiries, where "sign in" opens a test mailbox and
  // no real message exists to leak. That is a sandbox, and gating it behind a login only stops
  // people trying the demo.
  //
  // The moment a Graph client id is configured the page reads somebody's REAL Outlook, and the
  // login comes back on its own. The condition is deliberately this env var and not a separate
  // "is this a demo" flag: a flag can be set wrongly, whereas this one cannot be true unless the
  // mailbox genuinely is fake (see services/mailProvider.js, which picks the provider the same way).
  const graphClientId = String(env.REACT_APP_KRAPF_GRAPH_CLIENT_ID || '').trim();
  const forcedProvider = String(env.REACT_APP_KRAPF_MAIL_PROVIDER || '').trim().toLowerCase();
  const mockMailbox = forcedProvider === 'mock' || (!graphClientId && forcedProvider !== 'graph');
  return {
    tenantId: 'krapf',
    displayName: 'Krapf Bus Company',
    branding: BRANDING,
    applications: [
      {
        appId: 'krapf-charter-mail',
        type: 'charter-mail',
        route: '/krapfmail',
        aliases: [],
        displayName: 'Charter Mail',
        description: 'Charter inquiry inbox. Reads the company Outlook mailbox, pulls the trip details out of each inquiry, and drafts the reply asking for what is missing.',
        icon: 'chat',
        // Public ONLY against the mock mailbox (see mockMailbox above); staff-only the moment it can
        // reach a real one.
        access: { public: mockMailbox, identityPool: 'rideshare', anyOfGroups: KRAPF_OPS_GROUPS },
        runtime: { engine: 'ecap-flow', flowId: KRAPF_INTAKE_FLOW_ID, publicKeyConfigured: !!intakeKey },
        // Verified against movorra-flows/templates/krapf/krapf-charter-intake.flow.json: the flow has
        // no human://approve and no human://handoff action, so it creates no Approvals and no Live
        // conversations. Its sessions DO land in Studio Leads and Analytics.
        capabilities: { leads: true, analytics: true, approvals: false, liveHandoff: false },
        records: { studio: true, source: '/krapfmail' },
        status: intakeKey
          ? { configured: true }
          : { configured: false, reason: 'No public intake key is configured for this environment.' },
      },
    ],
    // Values routing needs but screens must never render.
    runtimeKeys: { 'krapf-charter-mail': intakeKey },
  };
}
